import {
  useBuildPermissions,
  useCurrentUserResource,
  useGetUserAuthenticated,
  usePermissions,
} from '@dispatch/shared/hooks';
import { User } from '@dispatch/shared/types';
import {
  RequireAuth,
  useAccessToken,
  useConfigureAuth,
  useCurrentUser,
} from '@dispatch/web/auth';
import { trackMetric, useSyncTelemetryUser } from '@dispatch/web/telemetry';
import { AppLayout, routes } from '@dispatch/web/ui';
import { useThemeMode } from '@dispatch/web/theme';
import { Container, Grid } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { Outlet } from 'react-router-dom';

export function App() {
  const { isAccessTokenSet } = useAccessToken();
  const { fullName, userId, tenantId } = useCurrentUser();
  const { userPermissions, status: permissionStatus } = useBuildPermissions(
    userId || '',
    isAccessTokenSet
  );
  const { permissions, setPermissions } = usePermissions();
  const { data, status: userStatus } = useGetUserAuthenticated(
    userId || '',
    isAccessTokenSet
  );
  const user: User | undefined = data?.data?.[0];

  const { resourceId, setResourceId, setIsAdjuster } = useCurrentUserResource();

  useEffect(() => {
    if (!permissions && userPermissions) {
      setPermissions(userPermissions);
    }
  }, [userPermissions, permissions, setPermissions]);

  useEffect(() => {
    if (!resourceId && user?.resourceId) {
      setResourceId(user?.resourceId);
      setIsAdjuster(true);
    }
  }, [resourceId, setIsAdjuster, setResourceId, user?.resourceId]);

  const status = useMemo(() => {
    if (permissionStatus === 'loading' || userStatus === 'loading') {
      return 'loading';
    }
    if (permissionStatus === 'success' && userStatus === 'success') {
      return 'success';
    }
    // Either bootstrap query is in an error/exhausted state — surface an
    // actionable error so the user gets a retry affordance, not an
    // indefinite spinner.
    if (permissionStatus === 'error' || userStatus === 'error') {
      return 'error';
    }

    return undefined;
  }, [permissionStatus, userStatus]);

  const queryClient = useQueryClient();
  const handleRetry = useCallback(() => {
    queryClient.invalidateQueries(['user']);
    queryClient.invalidateQueries(['user-permissions']);
  }, [queryClient]);

  // Measure how long the blocking (first-load) state persisted before it
  // resolved or errored, so the bootstrap experience can be monitored (AC #5).
  const blockingLoadStartRef = useRef<number | null>(null);
  useEffect(() => {
    if (status === 'loading' || status === undefined) {
      if (blockingLoadStartRef.current === null) {
        blockingLoadStartRef.current = Date.now();
      }
    } else if (blockingLoadStartRef.current !== null) {
      trackMetric(
        'BootstrapBlockingLoadMs',
        Date.now() - blockingLoadStartRef.current,
        { outcome: status }
      );
      blockingLoadStartRef.current = null;
    }
  }, [status]);

  useSyncTelemetryUser(userId, tenantId);
  useConfigureAuth();

  const { isDark, toggle } = useThemeMode();

  return (
    <RequireAuth>
      <AppLayout
        routes={routes}
        brandName="Dispatch"
        currentUserName={fullName || ''}
        status={status}
        permissions={permissions}
        user={user}
        onRetry={handleRetry}
        isDark={isDark}
        onToggleTheme={toggle}
      >
        <Container maxWidth={false} sx={{ mb: 1, padding: '0 !important' }}>
          <Grid container padding={0}>
            <Outlet />
            {/* Start rendering router matches */}
          </Grid>
        </Container>
      </AppLayout>
    </RequireAuth>
  );
}

export default App;
